/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, StrikeZone, ordinal, styles, PercentileChart, Table, pitchTypeDisplayName, BaseRunners, HitLocation, dateTranslate, number } from 'yankees-react-utils';
import { useOktaQueries, Player, Game, StrikeZones, usePitchVideoFn } from '../../../data';
import ReportCard from '../../../components/ReportCard';
import { toSwingDecisionSeries } from '../../../data/dataMapping';
import HitterTable from './HitterTable';
import { VideoPlayerModal } from '../../../components/VideoPlayer';
import VideoPlayerButton from '../../../components/VideoPlayerButton';
import PlayerAuthoredReportNote from '../../../components/Notes/PlayerAuthoredReportNote';
import usePlayerSelector from '../../../utils/usePlayerSelector';
import BPCard from './BPCard';
import usePlayerSettings from '../../../utils/usePlayerSettings';
import StatTooltip from '../../../components/StatTooltip';

const StyledSummaryTableWrapper = styled.div`
    &&& {
        font-size: 120%;
        table tr th.ab--videobtn { width: 1%; }
        table tr th.ab--abno { width: 1%; }
        table tr th.ab--inning { width: 1%; }
        table tr th.ab--situation { width: 5%; }
        table tr th.ab--runners { width: 5%; }
        table tr th.ab--pitch { width: 7%; }
        table tr th.ab--result { width: 10%; }
        table tr th.ab--hitloc { width: 10%; }
        table tr th.ab--swgrade { width: 2%; }
        table tr th.ab--notes { width: 40%; }
    }
`;

const NA = () => <div style={{ padding: '150px 0', fontSize: '200%', fontStyle: 'italic', textAlign: 'center' }}>N/A</div>;

const StyledReportCard = styled(ReportCard)`
    margin-top: 10px;
`;

const percentileChartShadeColor = (grade, doTextColor) => {
    // dark green
    if (grade >= 7.5) {
        return doTextColor ? '#FFF' : '#008A00';
    }
    // light green
    if (grade >= 5) {
        return doTextColor ? '#000' : '#90EE90';
    }

    return doTextColor ? '#000' : '#FFF';
};

const buildQueries = (playerid, gameid, gameinfo) => {
    const { yearid, levelid } = gameinfo;
    const queries = [
        {
            queryKey: ['player', playerid],
            queryFn: (t) => Player.get(t, { playerId: playerid }),
        },
        {
            queryKey: ['swdcsn', playerid, gameid],
            queryFn: (t) => Game.swingdecisions(t, {
                playerid,
                gameid,
                include30: true,
            }),
        },
        {
            queryKey: ['strikeZoneContoursLhb', playerid, gameid],
            queryFn: (t) => StrikeZones.getStrikeZoneContours(t, { islhb: true, yearid, levelid }),
        },
        {
            queryKey: ['strikeZoneContoursRhb', playerid, gameid],
            queryFn: (t) => StrikeZones.getStrikeZoneContours(t, { islhb: false, yearid, levelid }),
        },
    ];
    return queries;
};

const mapDataResponse = (response) => {
    const player = response[0].data || {};
    const swDecData = response[1].data || {};
    const contoursLhb = response[2].data || {};
    const contoursRhb = response[3].data || {};
    const pitchers = swDecData.pitchers.map((p, index) => ({ pitcherid: p.playerid, name: `${p.usesname} ${p.lastname}`, throws: p.throws, sortorder: index }));
    const swDecDataGame = swDecData && swDecData.gamescore.length > 0 ? swDecData.gamescore[0] : {};
    return { player, swDecData, pitchers, contoursLhb, contoursRhb, swDecDataGame };
};

const percentOnLine = (value, low, high) => {
    // return the percent 'across the line' a give value is, given the min and max of that line
    // if min is -10 and max is 10, the value 0 would return '50' for 50% of the way between the min and max
    let denom = high - low;
    if (denom === 0) {
        denom = 1;
    }
    return ((value - low) * 100) / denom;
};

const Hitter = ({ playerid, gameid, gameinfo, isInBats }) => {
    const { t: translate } = useTranslation();
    const userPlayer = usePlayerSelector();
    const pitchVideoFn = usePitchVideoFn();
    const { ShowGameScore } = usePlayerSettings();
    const dataQueries = buildQueries(playerid, gameid, gameinfo);
    const dataQueriesResponse = useOktaQueries(dataQueries);

    if (dataQueriesResponse.some((q) => q.isLoading)) {
        return <LoadingSpinner />;
    }

    const { player, swDecData, pitchers, contoursLhb, contoursRhb, swDecDataGame } = mapDataResponse(dataQueriesResponse);
    const ntileSections = [
        { id: 'SwDecGrade', title: translate('sections.post-game.hitter.swdec-gade'), value: swDecDataGame.swdecgrade },
        { id: 'ConGrade', title: translate('sections.post-game.hitter.con-grade'), value: swDecDataGame.msgrade },
        { id: 'CQGrade', title: translate('sections.post-game.hitter.cq-grade'), value: swDecDataGame.cqgrade },
        { id: 'TotalGrade', title: translate('sections.post-game.hitter.total-grade'), value: swDecDataGame.totalgrade },
    ];
    return swDecData && (
        <>
            <BPCard gameid={gameid} playerid={playerid} gamedate={gameinfo.gamedate} />
            {ShowGameScore && (
                <StyledReportCard>
                    <h2 className="text-start">
                        {translate('sections.post-game.hitter.game-score-components')}
                    </h2>
                    <div className="d-flex flex-row flex-wrap justify-content-around mt-1" style={{ fontSize: '120%' }}>
                        {ntileSections.filter((stat) => stat.value !== null).map((stat) => (
                            <div key={stat.id}>
                                <b>{stat.title}</b>
                                <PercentileChart
                                    percentilesToDisplay={[{
                                        id: stat.id,
                                        title: null,
                                        isActive: true,
                                    }]}
                                    hideMedianDisplay
                                    hideTooltip
                                    singleDataPoint={{
                                        value: number(stat.value, 1),
                                        name: stat.id,
                                        displayPercentOnLine: percentOnLine(stat.value, Math.min(0, stat.value), Math.max(10, stat.value)) + 5.745,
                                        fillColorHex: percentileChartShadeColor(stat.value, 0),
                                        textColorHex: percentileChartShadeColor(stat.value, 1),
                                    }}
                                    labelLeft="0"
                                    labelMiddle="5"
                                    labelRight="10"
                                    svgWidth={180}
                                    labelWidth={0}
                                    statlineWidth={180}
                                    showValueOnPoint
                                />
                            </div>
                        ))}
                        {ntileSections.filter((stat) => stat.value !== null).length === 0 && (<NA />)}
                    </div>
                </StyledReportCard>
            )}

            {pitchers.length === 0 ? <NA /> : ''}
            {pitchers.length > 0 && (
                <StyledReportCard>
                    <h2 className="text-start">
                        {translate('sections.post-game.hitter.at-bat-summary')}
                        <VideoPlayerButton
                            className="ms-3"
                            color={styles.theme.background.astronaut}
                            icon={translate('common.all')}
                            gameid={gameid}
                            batterid={playerid}
                        />
                    </h2>
                    <StyledSummaryTableWrapper>
                        <Table size="sm">
                            {pitchers.map((pitcher) => (
                                <tbody key={pitcher.pitcherid}>
                                    <tr>
                                        <th className="table-title">
                                            {pitcher.name} ({pitcher.throws === 1 ? translate('baseball.rhp') : translate('baseball.lhp')})
                                        </th>
                                        <th className="table-title" colSpan={99} />
                                    </tr>
                                    <tr>
                                        {isInBats && (
                                            <th className="ab--videobtn">
                                                <VideoPlayerButton
                                                    className="ms-1"
                                                    color={styles.theme.background.astronaut}
                                                    icon={translate('common.all')}
                                                    gameid={gameid}
                                                    batterid={playerid}
                                                    pitcherid={pitcher.pitcherid}
                                                />
                                            </th>
                                        )}
                                        <th className="ab--abno">{translate('baseball.abbr-atbat')}</th>
                                        <th className="ab--inning">{translate('baseball.inning')}</th>
                                        <th className="ab--situation">{translate('baseball.count')}/{translate('baseball.outs_other')}</th>
                                        <th className="ab--runners">{translate('baseball.runners_other')}</th>
                                        <th className="ab--pitch">{translate('baseball.pitches_one')}</th>
                                        <th className="ab--result">{translate('common.result')}</th>
                                        <th className="ab--hitloc">{translate('baseball.hit-location')}</th>
                                        {ShowGameScore
                                        && (
                                            <>
                                                <th className="ab--swgrade"><StatTooltip statistic="SwDecGrd">{translate('baseball.abbr-swingdecision')}</StatTooltip></th>
                                                <th className="ab--swgrade"><StatTooltip statistic="ConGrd">{translate('sections.post-game.hitter.abbr-ms-grade')}</StatTooltip></th>
                                                <th className="ab--swgrade"><StatTooltip statistic="CQGrd">{translate('baseball.abbr-contactquality')}</StatTooltip></th>
                                                <th className="ab--swgrade"><StatTooltip statistic="GSGrd">{translate('common.total')}</StatTooltip></th>
                                            </>
                                        )}
                                        <th className="ab--notes">{translate('components.notes.notes')}</th>
                                    </tr>
                                    {swDecData.abs.filter((ab) => ab.pitcherid === pitcher.pitcherid).map((ab) => (
                                        <tr key={`ab-${pitcher.pitcherid}-${ab.abno}`} style={{ height: '1px' }}>
                                            {isInBats && (<td><VideoPlayerButton className="ms-1" gameid={gameid} eventseq={ab.eventseq} batterid={playerid} pitcherid={pitcher.pitcherid} /></td>)}
                                            <td>{ab.abno}</td>
                                            <td>{ab.inning}</td>
                                            <td>{ab.balls}-{ab.strikes}, {ab.outsbefore} out</td>
                                            <td><BaseRunners baseState={ab.bcbefore} height={20} width={30} emptyColor="#afafaf" /></td>
                                            <td>
                                                {pitchTypeDisplayName(ab, true, true, translate)}
                                                {ab.velocity && `, ${translate('format.abbr-mph', { value: number(ab.velocity) })}`}
                                                <div style={{ zoom: '70%' }}>
                                                    <StrikeZone
                                                        size="xs"
                                                        showbatter={false}
                                                        seriesData={[{ marker: { radius: 3 }, data: [{ x: ab.platex, y: ab.platez }] }]}
                                                        pitcherView={false}
                                                        showoffgrid
                                                    />
                                                </div>
                                            </td>
                                            <td>
                                                {ab.baseevent_desc}
                                                <div>
                                                    {ab.hittype_desc} {ab.exitvelo && (ab.tm_imputed ? '(--)' : `(${translate('format.abbr-mph', { value: number(ab.exitvelo) })})`)}
                                                </div>
                                            </td>
                                            <td>
                                                {ab.ballinplayx !== null && ab.ballinplayy !== null && (
                                                    <div style={{ zoom: '60%' }}>
                                                        <HitLocation
                                                            ballinplayx={ab.ballinplayx}
                                                            ballinplayy={ab.ballinplayy}
                                                            ishr={ab.ishr}
                                                        />
                                                    </div>
                                                )}
                                            </td>
                                            {ShowGameScore && (
                                                <>
                                                    <td>{number(ab.swdecgrade, 1)}</td>
                                                    <td>{number(ab.msgrade, 1)}</td>
                                                    <td>{number(ab.cqgrade, 1)}</td>
                                                    <td>{number(ab.totalgrade, 1)}</td>
                                                </>
                                            )}
                                            {ab.abno === 1 && (
                                                <td
                                                    rowSpan={swDecData.abs.filter((xab) => xab.pitcherid === pitcher.pitcherid).length}
                                                    style={{ backgroundColor: 'white', height: 'inherit' }}
                                                >
                                                    <PlayerAuthoredReportNote
                                                        className="h-100 mt-0"
                                                        type="mynt"
                                                        section="2"
                                                        player={userPlayer}
                                                        pitcher={{ PlayerID: pitcher.pitcherid, FullName: pitcher.name }}
                                                        gameId={gameid}
                                                        compact
                                                        customTitleForModal={`${pitcher.name} ${dateTranslate(translate, gameinfo.gamedate)}`}
                                                        customPlaceholder={translate('components.notes.enter-pitcher-game-notes')}
                                                        hideWhenNotFound
                                                    />
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            ))}
                        </Table>

                        <PlayerAuthoredReportNote
                            type="mynt"
                            section="4"
                            compact
                            player={userPlayer}
                            gameId={gameid}
                            customTitleForModal={`Game Notes ${dateTranslate(translate, gameinfo.gamedate)}`}
                            customPlaceholder="Tap to enter game notes..."
                            hideWhenNotFound
                        />
                    </StyledSummaryTableWrapper>
                </StyledReportCard>
            )}

            {swDecData.abs.length > 0 && (
                <StyledReportCard>
                    <div className="sd-legend">
                        <span>
                            <svg className="me-1 mb-1"><circle fill="transparent" stroke="#333333" cx="6" cy="6" r="5" strokeWidth="2" /></svg>
                            <h4>{translate('sections.post-game.hitter.take')}</h4>
                        </span>
                        <span>
                            <svg className="me-1 ms-2 mb-1" viewBox="-1 -1 12 12"><path fill="transparent" stroke="#333333" d="M 5 0 L 10 5 5 10 0 5 5 0 Z" strokeWidth="2" /></svg>
                            <h4>{translate('sections.post-game.hitter.swing')}</h4>
                        </span>
                    </div>
                    <div className="d-flex flex-wrap justify-content-left">
                        {swDecData.abs.map((ab) => {
                            const pitcher = pitchers.find((p) => p.pitcherid === ab.pitcherid);
                            const showlhb = player.Bats === 2 || (player.Bats === 3 && pitcher.throws === 1);
                            const strikeZoneContours = showlhb ? contoursLhb : contoursRhb;
                            const pitches = swDecData.decisions
                                .filter((dec) => dec.eventseq === ab.eventseq && dec.pitcherid === ab.pitcherid)
                                .sort((a, b) => a.pitchseq - b.pitchseq);
                            const series = toSwingDecisionSeries(translate, pitches, ShowGameScore);
                            return (
                                <ReportCard key={ab.eventseq} className="m-2">
                                    <h4>
                                        {pitcher.name} ({pitcher.throws === 1 ? translate('baseball.rhp') : translate('baseball.lhp')}) AB #{ab.abno}
                                    </h4>
                                    <h5>
                                        {ab.istop ? translate('baseball.abbr-top') : translate('baseball.abbr-bottom')} {ordinal(ab.inning)}, {ab.baseevent_desc || ab.hittype_desc}
                                        {isInBats && (<VideoPlayerButton className="ms-1" gameid={gameid} eventseq={ab.eventseq} batterid={playerid} pitcherid={pitcher.pitcherid} />)}
                                    </h5>
                                    <StrikeZone
                                        enableZoom={false}
                                        contourPoints={pitcher.throws === 1 ? strikeZoneContours.rhp : strikeZoneContours.lhp}
                                        seriesData={series}
                                        size="lg"
                                        height={400}
                                        showlhb={showlhb}
                                        showrhb={!showlhb}
                                        yopposite={showlhb}
                                        showoffgrid
                                        pitchVideoFn={isInBats ? pitchVideoFn : null}
                                        videoPlayerModal={VideoPlayerModal}
                                        pitcherView={false}
                                    />
                                    <HitterTable pitches={pitches} isInBats={isInBats} />
                                </ReportCard>
                            );
                        })}
                    </div>
                </StyledReportCard>
            )}
        </>
    );
};

Hitter.propTypes = {
    playerid: PropTypes.number.isRequired,
    gameid: PropTypes.number.isRequired,
    gameinfo: PropTypes.shape({
        gameid: PropTypes.number,
        gamedate: PropTypes.string,
        levelid: PropTypes.number,
    }).isRequired,
    isInBats: PropTypes.bool.isRequired,
};

export default Hitter;
